// src/admin/csvParsers.js
//
// CSV IMPORT MIGRATION (CLAUDE.md V4 locked #6): these parsers are migrated
// VERBATIM from prototype/between-the-lines-v2.html L1654-1859 -- the working,
// battle-tested NotebookLM CSV workflow. Logic is byte-for-byte identical to the
// prototype; only `const` -> `export const` was added. DO NOT rewrite/reimplement.
//
// Supports MCQ (9 cols), Flashcard (2 cols), Fill (5 cols), Matching (4-10 cols),
// with header auto-detection (NotebookLM exports include a header row).
// The admin CsvImportDialog (a later WP-4 slice) consumes these.

export const parseCSVLine = (line) => {
    const fields = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    while (i < line.length) {
        const char = line[i];
        if (inQuotes) {
            if (char === '"' && line[i + 1] === '"') { current += '"'; i += 2; }
            else if (char === '"') { inQuotes = false; i++; }
            else { current += char; i++; }
        } else {
            if (char === '"') { inQuotes = true; i++; }
            else if (char === ',') { fields.push(current.trim()); current = ''; i++; }
            else { current += char; i++; }
        }
    }
    fields.push(current.trim());
    return fields;
};

export const parseCSVText = (text) => {
    // Strip UTF-8 BOM
    if (text.charCodeAt(0) === 0xFEFF) text = text.substring(1);
    // Normalize line endings
    text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    // Split + filter empty trailing lines
    const lines = text.split('\n').filter(l => l.trim().length > 0);
    return lines.map(parseCSVLine);
};

// Parse "B. Full text" -> 1 (returns -1 if invalid)
export const parseCorrectLetter = (str) => {
    if (!str) return -1;
    const match = str.trim().match(/^([A-D])[.:]\s*/i);
    if (!match) return -1;
    return match[1].toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0);
};

export const validateQuizRow = (row, rowIdx) => {
    const errors = [];
    const warnings = [];
    if (row.length !== 9) {
        errors.push(`Expected 9 columns, got ${row.length}`);
        return { valid: false, errors, warnings, data: null, rowIdx };
    }
    const [num, question, hint, optA, optB, optC, optD, correctRaw, rationale] = row;
    if (!question) errors.push('Question text empty');
    if (!optA) errors.push('Option A empty');
    if (!optB) errors.push('Option B empty');
    if (!optC) errors.push('Option C empty');
    if (!optD) errors.push('Option D empty');
    const correctIdx = parseCorrectLetter(correctRaw);
    if (correctIdx < 0) errors.push(`Correct Answer must start with A./B./C./D. — got "${(correctRaw || '').substring(0, 30)}..."`);
    if (question && question.length > 1000) warnings.push('Question > 1000 chars');
    if (!hint) warnings.push('Hint empty');
    if (!rationale) warnings.push('Rationale empty');
    const data = errors.length === 0 ? {
        type: 'mcq',
        question, hint, options: [optA, optB, optC, optD], correct: correctIdx, explanation: rationale
    } : null;
    return { valid: errors.length === 0, errors, warnings, data, rowIdx };
};

export const validateFlashcardRow = (row, rowIdx) => {
    const errors = [];
    const warnings = [];
    if (row.length !== 2) {
        errors.push(`Expected 2 columns, got ${row.length}`);
        return { valid: false, errors, warnings, data: null, rowIdx };
    }
    const [front, back] = row;
    if (!front) errors.push('Front text empty');
    if (!back) errors.push('Back text empty');
    if (front && front.length > 500) warnings.push('Front > 500 chars');
    if (back && back.length > 500) warnings.push('Back > 500 chars');
    const data = errors.length === 0 ? { front, back } : null;
    return { valid: errors.length === 0, errors, warnings, data, rowIdx };
};

export const parseQuizCSV = (text) => {
    const allRows = parseCSVText(text);
    if (allRows.length < 2) return { headerOk: false, error: 'CSV is empty or has only header', rows: [], validCount: 0, errorCount: 0 };
    const header = allRows[0].map(h => h.toLowerCase().replace(/[^a-z]/g, ''));
    const headerOk = header.length === 9 && header[1] === 'question' && header[7].includes('correct');
    if (!headerOk) return { headerOk: false, error: 'Quiz CSV header mismatch. Expected: #, Question, Hint, Option A, Option B, Option C, Option D, Correct Answer, Rationale', rows: [], validCount: 0, errorCount: 0 };
    const dataRows = allRows.slice(1);
    const rows = dataRows.map((row, idx) => validateQuizRow(row, idx + 2));
    const validCount = rows.filter(r => r.valid).length;
    const errorCount = rows.length - validCount;
    return { headerOk: true, rows, validCount, errorCount };
};

// Sprint 2.5: detect if first row is a header (NotebookLM exports include "Flashcard Front,Flashcard Back")
export const isFlashcardHeaderRow = (row) => {
    if (!row || row.length !== 2) return false;
    const [a, b] = row.map(c => (c || '').toLowerCase().trim());
    const headerWords = ['front', 'back', 'flashcard front', 'flashcard back', 'term', 'definition', 'question', 'answer'];
    return headerWords.includes(a) && headerWords.includes(b);
};

export const parseFlashcardCSV = (text) => {
    const allRows = parseCSVText(text);
    if (allRows.length < 1) return { rows: [], validCount: 0, errorCount: 0 };
    // Sprint 2.5 fix: skip header row if detected (backward compatible — Sprint 2.0 no-header files unaffected)
    const dataRows = isFlashcardHeaderRow(allRows[0]) ? allRows.slice(1) : allRows;
    const rows = dataRows.map((row, idx) => validateFlashcardRow(row, idx + 1));
    const validCount = rows.filter(r => r.valid).length;
    const errorCount = rows.length - validCount;
    return { rows, validCount, errorCount };
};

// ============================================================
// Sprint 2.5: Fill + Matching parsers + Auto-detect dispatcher
// ============================================================

// Validate a Fill row (5 columns: #, Sentence, Answer, Hint, Acceptable)
export const validateFillRow = (row, rowIdx) => {
    const errors = [];
    const warnings = [];
    if (row.length !== 5) {
        errors.push(`Expected 5 columns, got ${row.length}`);
        return { valid: false, errors, warnings, data: null, rowIdx };
    }
    const [num, sentence, answer, hint, acceptableRaw] = row.map(c => (c == null ? '' : c));
    if (!sentence) errors.push('Sentence empty');
    if (sentence && !/_{3,}/.test(sentence)) errors.push('Sentence must contain a gap marker (_____)');
    if (!answer) errors.push('Answer empty');
    const acceptable = acceptableRaw ? acceptableRaw.split(';').map(s => s.trim()).filter(Boolean) : undefined;
    const data = errors.length === 0 ? {
        type: 'fill', sentence, answer, hint: hint || '', acceptable
    } : null;
    return { valid: errors.length === 0, errors, warnings, data, rowIdx };
};

// Validate a Matching row (<=10 columns: #, Instruction, then up to 4 Left/Right pairs)
export const validateMatchingRow = (row, rowIdx) => {
    const errors = [];
    const warnings = [];
    if (row.length > 10) {
        errors.push(`Expected ≤10 columns, got ${row.length}`);
        return { valid: false, errors, warnings, data: null, rowIdx };
    }
    const padded = [...row, ...Array(Math.max(0, 10 - row.length)).fill('')];
    const [num, instruction, ...pairCols] = padded;
    const inst = (instruction || '').trim();
    if (!inst) errors.push('Instruction empty');
    const pairs = [];
    for (let i = 0; i < 4; i++) {
        const left = (pairCols[i * 2] || '').trim();
        const right = (pairCols[i * 2 + 1] || '').trim();
        if (left && right) pairs.push({ left, right });
        else if (left || right) errors.push(`Pair ${i + 1} has only one side filled`);
    }
    if (pairs.length < 2) errors.push(`Need ≥2 pairs, got ${pairs.length}`);
    const data = errors.length === 0 ? {
        type: 'matching', instruction: inst, pairs
    } : null;
    return { valid: errors.length === 0, errors, warnings, data, rowIdx };
};

// Parse Fill CSV
export const parseFillCSV = (text) => {
    const allRows = parseCSVText(text);
    if (allRows.length < 2) return { headerOk: false, error: 'CSV is empty or has only header', rows: [], validCount: 0, errorCount: 0 };
    const header = allRows[0].map(h => (h || '').toLowerCase().replace(/[^a-z]/g, ''));
    const headerOk = header.length === 5 && header[1] === 'sentence' && header[2] === 'answer';
    if (!headerOk) return { headerOk: false, error: 'Fill CSV header mismatch. Expected: #, Sentence, Answer, Hint, Acceptable', rows: [], validCount: 0, errorCount: 0 };
    const dataRows = allRows.slice(1);
    const rows = dataRows.map((row, idx) => validateFillRow(row, idx + 2));
    const validCount = rows.filter(r => r.valid).length;
    return { headerOk: true, rows, validCount, errorCount: rows.length - validCount };
};

// Parse Matching CSV
export const parseMatchingCSV = (text) => {
    const allRows = parseCSVText(text);
    if (allRows.length < 2) return { headerOk: false, error: 'CSV is empty or has only header', rows: [], validCount: 0, errorCount: 0 };
    const header = allRows[0].map(h => (h || '').toLowerCase().replace(/[^a-z]/g, ''));
    const headerOk = header.length >= 4 && header[1] === 'instruction' && (header[2] || '').startsWith('pair');
    if (!headerOk) return { headerOk: false, error: 'Matching CSV header mismatch. Expected: #, Instruction, Pair1Left, Pair1Right, ...', rows: [], validCount: 0, errorCount: 0 };
    const dataRows = allRows.slice(1);
    const rows = dataRows.map((row, idx) => validateMatchingRow(row, idx + 2));
    const validCount = rows.filter(r => r.valid).length;
    return { headerOk: true, rows, validCount, errorCount: rows.length - validCount };
};

// Detect quiz CSV format from header row
export const detectQuizFormat = (text) => {
    const allRows = parseCSVText(text);
    if (allRows.length < 1) return null;
    const header = allRows[0].map(h => (h || '').toLowerCase().replace(/[^a-z]/g, ''));
    if (header.length === 9 && header[1] === 'question' && (header[7] || '').includes('correct')) return 'mcq';
    if (header.length === 5 && header[1] === 'sentence' && header[2] === 'answer') return 'fill';
    if (header.length >= 4 && header[1] === 'instruction' && (header[2] || '').startsWith('pair')) return 'matching';
    return null;
};

// Unified quiz CSV parser dispatcher (auto-detects MCQ / Fill / Matching)
export const parseQuizCSVAuto = (text) => {
    const format = detectQuizFormat(text);
    if (format === 'mcq')      return { format: 'mcq',      ...parseQuizCSV(text) };
    if (format === 'fill')     return { format: 'fill',     ...parseFillCSV(text) };
    if (format === 'matching') return { format: 'matching', ...parseMatchingCSV(text) };
    return { format: null, headerOk: false, error: 'Unrecognized CSV format. Expected one of: MCQ (9 cols), Fill (5 cols), Matching (4-10 cols)', rows: [], validCount: 0, errorCount: 0 };
};
